import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Trash2, UserPlus2 } from 'lucide-react';
import { AutocompleteSelect } from '../../../shared/components/AutocompleteSelect';
import { Alert } from '../../../shared/components/Alert';
import { Skeleton } from '../../../shared/components/Skeleton';
import { ConfirmDialog } from '../../../shared/components/ConfirmDialog';
import { getFriendlyErrorMessage } from '../../../shared/lib/errors';
import { listarUsuarios } from '../../usuarios/api';
import { ActionModal } from './ActionModal';
import {
  useAdicionarParticipante,
  useParticipantes,
  usePapeisParticipante,
  useRemoverParticipante,
} from '../hooks';
import type { OpportunityParticipant } from '../api';

interface Props {
  opportunityId: string;
  responsavelId: string;
  canEdit: boolean;
}

/**
 * Seção de participantes internos (US-COM-004 §Participantes Internos
 * Multiárea). Autor de destaque: o responsável comercial NÃO precisa
 * ser adicionado (é participante implícito) — o backend rejeita 422
 * se tentar, e o frontend filtra o responsavelId do picker de usuários.
 */
export const OpportunityParticipantsSection: React.FC<Props> = ({
  opportunityId,
  responsavelId,
  canEdit,
}) => {
  const { data, isLoading, isError, error } = useParticipantes(opportunityId);
  const { data: roles } = usePapeisParticipante();
  const remove = useRemoverParticipante(opportunityId);
  const [addOpen, setAddOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<OpportunityParticipant | null>(null);
  const [rowError, setRowError] = useState<string | null>(null);

  const { data: usuariosResp } = useQuery({
    queryKey: ['picker', 'usuarios'],
    queryFn: () => listarUsuarios({ limit: 200 }),
    staleTime: 60 * 1000,
  });

  const roleLabel = useMemo(() => {
    const m = new Map<string, string>();
    (roles ?? []).forEach((r) => m.set(r.id, r.label));
    return m;
  }, [roles]);

  const userById = useMemo(() => {
    const m = new Map<string, { name: string; email: string }>();
    (usuariosResp?.data ?? []).forEach((u) => m.set(u.id, { name: u.name, email: u.email }));
    return m;
  }, [usuariosResp]);

  const responsavelInfo = userById.get(responsavelId);

  return (
    <section className="mb-6 rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-700">
          Participantes internos
        </h2>
        {canEdit && (
          <button
            onClick={() => setAddOpen(true)}
            className="inline-flex items-center gap-1 rounded-md border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700 hover:bg-blue-100"
          >
            <UserPlus2 className="h-3 w-3" />
            Adicionar
          </button>
        )}
      </div>

      {rowError && (
        <Alert variant="error" className="mb-2">
          {rowError}
        </Alert>
      )}

      {isLoading && (
        <div className="space-y-2">
          {Array.from({ length: 2 }).map((_, i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </div>
      )}
      {isError && (
        <Alert variant="error">
          Erro ao carregar participantes:{' '}
          {(error as Error)?.message ?? 'Falha desconhecida.'}
        </Alert>
      )}

      {data && data.length === 0 && (
        <p className="text-sm text-gray-500">
          Só o responsável comercial
          {responsavelInfo ? (
            <>
              {' '}
              (<span className="font-medium">{responsavelInfo.name}</span>)
            </>
          ) : null}{' '}
          tem acesso garantido. Adicione participantes de outras áreas para
          trabalhar em conjunto.
        </p>
      )}

      {data && data.length > 0 && (
        <ul className="divide-y divide-gray-100">
          {data.map((p) => {
            const info = userById.get(p.userId);
            return (
            <li key={p.id} className="flex items-center justify-between py-2">
              <div className="min-w-0 flex-1">
                <div className="text-sm text-gray-900">
                  {info?.name ?? <span className="text-gray-400">Usuário não encontrado</span>}
                  {info?.email && (
                    <span className="ml-2 text-xs text-gray-500">{info.email}</span>
                  )}
                </div>
                <div className="text-xs text-gray-500">
                  Papel:{' '}
                  <span className="font-medium">
                    {roleLabel.get(p.roleId) ?? p.roleId}
                  </span>
                </div>
              </div>
              {canEdit && (
                <button
                  type="button"
                  onClick={() => {
                    setRowError(null);
                    setPendingDelete(p);
                  }}
                  className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600"
                  title="Remover"
                  aria-label="Remover participante"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </li>
            );
          })}
        </ul>
      )}

      {addOpen && (
        <AddParticipantModal
          opportunityId={opportunityId}
          excludeUserId={responsavelId}
          onClose={() => setAddOpen(false)}
        />
      )}

      {pendingDelete && (
        <ConfirmDialog
          open
          title="Remover participante"
          message={`Remover ${userById.get(pendingDelete.userId)?.name ?? 'este participante'} da oportunidade? Ele perderá o acesso de leitura se ela for confidencial.`}
          confirmLabel="Remover"
          danger
          busy={remove.isPending}
          onConfirm={async () => {
            try {
              await remove.mutateAsync(pendingDelete.id);
              setPendingDelete(null);
            } catch (e) {
              setRowError(getFriendlyErrorMessage(e, 'Falha ao remover participante.'));
              setPendingDelete(null);
            }
          }}
          onCancel={() => setPendingDelete(null)}
        />
      )}
    </section>
  );
};

const AddParticipantModal: React.FC<{
  opportunityId: string;
  excludeUserId: string;
  onClose: () => void;
}> = ({ opportunityId, excludeUserId, onClose }) => {
  const [userId, setUserId] = useState('');
  const [roleId, setRoleId] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const add = useAdicionarParticipante(opportunityId);
  const { data: roles } = usePapeisParticipante();

  const { data: usuariosResp } = useQuery({
    queryKey: ['picker', 'usuarios'],
    queryFn: () => listarUsuarios({ limit: 200 }),
    staleTime: 60 * 1000,
  });

  const usuarioOptions = useMemo(
    () =>
      (usuariosResp?.data ?? [])
        .filter((u) => u.id !== excludeUserId)
        .map((u) => ({ value: u.id, label: u.name, description: u.email })),
    [usuariosResp, excludeUserId],
  );
  const roleOptions = useMemo(
    () =>
      (roles ?? []).map((r) => ({ value: r.id, label: r.label })),
    [roles],
  );

  const submit = async () => {
    setErr(null);
    if (!userId || !roleId) return;
    try {
      await add.mutateAsync({ userId, roleId });
      onClose();
    } catch (e) {
      setErr(getFriendlyErrorMessage(e, 'Falha ao adicionar participante.'));
    }
  };

  return (
    <ActionModal
      isOpen
      onClose={onClose}
      onSubmit={submit}
      title="Adicionar participante"
      description="O participante ativo ganha acesso de leitura mesmo em oportunidade confidencial."
      submitLabel="Adicionar"
      submitVariant="primary"
      isSubmitting={add.isPending}
      submitError={err}
      canSubmit={!!userId && !!roleId}
    >
      <div className="space-y-3">
        <AutocompleteSelect
          label="Usuário"
          options={usuarioOptions}
          value={userId}
          onChange={setUserId}
        />
        <AutocompleteSelect
          label="Papel"
          options={roleOptions}
          value={roleId}
          onChange={setRoleId}
        />
      </div>
    </ActionModal>
  );
};
